import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import client, { makePaginatedGet } from "@culturehq/client";

import { useClickTransport } from "app/context";
import { errorService, seo, t } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import { QueryStringProvider, QueryStringConsumer } from "app/context/QueryStringContext";

import PeopleCards from "./community/PeopleCards";
import CommunityFilters from "./community/CommunityFilters";
import CommunitySearchBar from "./community/CommunitySearchBar";
import CommunityPanels from "./community/CommunityPanels";
import { communityOptions } from "./community/filters/CommunityFilter";

const queryToOptions = queryString => {
  const { c, i, d, l, s, cl, cy, p, de, co } = queryString;

  return {
    community: Object.prototype.hasOwnProperty.call(communityOptions, c) ? c : null,
    interestIds: i || null,
    departmentIds: d || null,
    locationIds: l || null,
    skillIds: s || null,
    clubIds: cl || null,
    classYearIds: cy || null,
    programIds: p || null,
    degreeIds: de || null,
    companyIds: co || null
  };
};

const queryStringParsers = {
  c: "string",
  i: "intList",
  d: "intList",
  l: "intList",
  s: "intList",
  cl: "intList",
  cy: "intList",
  p: "intList",
  de: "intList",
  co: "intList"
};

class CommunityContext extends Component {
  constructor(props) {
    super(props);

    this.state = {
      usersState: [],
      pagination: {},
      options: queryToOptions(props.queryString),
      page: 1,
      loading: true,
      query: null,
      redirectToEvent: false,
      layout: "list"
    };
  }

  async componentDidMount() {
    this.fetchUsers();
    seo({
      title: t("Community - CultureHQ.com")
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { queryString } = this.props;
    const { options, page, query } = this.state;

    if (prevProps.queryString !== queryString) {
      this.setState({ options: queryToOptions(queryString), page: 1 });
    }

    if (prevState.options !== options) {
      this.fetchUsers();
    }

    if (prevState.page !== page && !query) {
      this.fetchUsers();
    }
  }

  handleCreateGroupEvent = async () => {
    const { onUsersClicked } = this.props;
    const { options, query, usersState } = this.state;
    this.setState({ loading: true });

    try {
      const { users, pagination } = await makePaginatedGet("users", "/users", options);

      // if query is empty, use the filters, in other case, use the filtered values
      if (!query) {
        onUsersClicked(users, pagination, options);
      } else {
        onUsersClicked(usersState, { totalCount: usersState.length }, options);
      }
      this.setState({ redirectToEvent: true, loading: false });
    } catch (error) {
      errorService.error(error);
      this.setState({ loading: false });
    }
  };

  handlePageSelected = page => {
    this.setState({ page });
    window.scrollTo(0, 0);
  };

  handleResults = ({ users, pagination, query }) => {
    if (!query) {
      this.fetchUsers();
      this.setState({ query });
    } else {
      this.setState({ usersState: users, pagination, query });
    }
  };

  fetchUsers = async () => {
    this.setState({ loading: true });
    const { options, page } = this.state;

    try {
      const { users, pagination } = await client.listUsers({ ...options, page });
      this.setState({ usersState: users, pagination, loading: false });
    } catch (error) {
      errorService.error(error);
      this.setState({ loading: false });
    }
  };

  handleChange = changedUser => {
    this.setState(({ usersState }) => ({
      usersState: usersState.map(user => (user.id === changedUser.id ? changedUser : user))
    }));
  };

  handleLayoutChange = nextLayout => {
    this.fetchUsers();
    this.setState({ layout: nextLayout });
  };

  render() {
    const {
      layout,
      loading,
      options,
      page,
      pagination,
      query,
      redirectToEvent,
      usersState
    } = this.state;

    if (redirectToEvent) {
      return <Redirect to="/events/new/type" />;
    }

    return (
      <div className="People">
        <section role="complementary" aria-label={t("Left bar - Filters")}>
          <div className="Filters SidePanel col-sm-12 col-sm-8 col-md-9 col-lg-2">
            <CommunityFilters
              layout={layout}
              loading={loading}
              onCreateGroupEvent={this.handleCreateGroupEvent}
              onLayoutChange={this.handleLayoutChange}
              options={options}
              pagination={pagination}
              query={query}
            />
          </div>
        </section>
        <section role="main">
          <div className="People__cards Central--column col-xs-12 col-sm-8 col-md-9 col-lg-7">
            <CommunitySearchBar
              onResults={this.handleResults}
              options={options}
              page={page}
            />
            <ErrorBoundary>
              <PeopleCards
                users={usersState}
                loading={loading}
                pagination={pagination}
                onPageSelected={this.handlePageSelected}
                onChange={this.handleChange}
                layout={layout}
              />
            </ErrorBoundary>
          </div>
        </section>
        <section role="complementary" aria-label={t("Right bar -Top interests and skills")}>
          <div className="People__right-column hidden-xs col-sm-4 col-md-3 col-lg-3">
            <CommunityPanels options={options} />
          </div>
        </section>
      </div>
    );
  }
}

const Community = props => {
  const { onUsersClicked } = useClickTransport();

  return (
    <QueryStringProvider parsers={queryStringParsers}>
      <QueryStringConsumer>
        {queryStringContext => (
          <CommunityContext
            {...props}
            {...queryStringContext}
            onUsersClicked={onUsersClicked}
          />
        )}
      </QueryStringConsumer>
    </QueryStringProvider>
  );
};

export default Community;
